import { Activity, Clock, FlaskConical, Percent, Timer, Wallet } from "lucide-react";

import { Card } from "@/components/ui/card";
import { formatEuro } from "@/lib/format";
import { methodTestStats } from "@/lib/testdata";
import { getMethodWalkthrough, type MethodWalkthrough } from "@/content/method-walkthroughs";
import { getPublicCasinos } from "@/lib/queries";
import { Link } from "@tanstack/react-router";

function Stat({ icon: Icon, value, label }: { icon: typeof Timer; value: string; label: string }) {
  return (
    <Card className="card-surface p-4">
      <Icon className="h-5 w-5 text-success" aria-hidden />
      <p className="mt-3 text-2xl font-bold tracking-tight text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </Card>
  );
}

/**
 * Volledig testrapport voor één betaalmethode: protocol, kerncijfers en de
 * complete meetreeks per casino.
 */
export function MethodTestReport({
  methodSlug,
  methodName,
  payoutViaBank = false,
}: {
  methodSlug: string;
  methodName: string;
  /** Methode kent geen eigen opname: winst wordt teruggeboekt naar de bank. */
  payoutViaBank?: boolean;
}) {
  const stats = methodTestStats(methodSlug);
  const walkthrough = getMethodWalkthrough(methodSlug);
  if (stats.tests === 0) {
    return walkthrough ? <Walkthrough methodName={methodName} w={walkthrough} /> : null;
  }
  const testedSlugs = new Set(stats.rows.map((r) => r.casinoSlug));
  const untested = getPublicCasinos()
    .filter((c) => c.links.some((l) => l.method?.slug === methodSlug))
    .filter((c) => !testedSlugs.has(c.slug));

  const maxDeposit = Math.max(...stats.rows.map((r) => r.depositSeconds));

  return (
    <section aria-labelledby="testrapport" className="scroll-mt-28">
      <h2 id="testrapport" className="text-xl font-semibold text-foreground">
        Testrapport: {methodName} in de praktijk
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
        Onze redactie deed {stats.tests} echte transacties met {methodName} bij {stats.casinos} aanbieders,
        met eigen geld en eigen accounts, tussen {stats.firstTestedAt} en {stats.lastTestedAt}. We meten
        de tijd tot creditering met een stopwatch vanaf het moment van bevestiging, en de uitbetaaltijd
        vanaf het indienen van het opnameverzoek tot bijschrijving.
      </p>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={FlaskConical} value={`${stats.tests}`} label={`Transacties getest met ${methodName}`} />
        <Stat
          icon={Timer}
          value={`${stats.medianDepositSeconds ?? "–"} sec.`}
          label={`Mediane stortingstijd (${stats.fastestDepositSeconds}–${stats.slowestDepositSeconds} sec.)`}
        />
        <Stat
          icon={Clock}
          value={stats.withdrawalSupportedInTests ? `${stats.medianWithdrawalHours} uur` : "n.v.t."}
          label={
            !stats.withdrawalSupportedInTests
              ? "Uitbetaling loopt via bank of wallet"
              : payoutViaBank
                ? `Mediane terugbetaling naar je bankrekening (${stats.fastestWithdrawalHours}–${stats.slowestWithdrawalHours} uur)`
                : `Mediane uitbetaling (${stats.fastestWithdrawalHours}–${stats.slowestWithdrawalHours} uur)`
          }
        />
        <Stat icon={Percent} value={`${stats.kycRate}%`} label="Van de tests vroeg om identificatie (KYC)" />
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <Stat icon={Activity} value={`${stats.successRate}%`} label="Geslaagd zonder handmatige tussenkomst" />
        <Stat icon={Wallet} value={formatEuro(stats.totalStaked)} label="Totaal ingelegd tijdens deze testronde" />
        <Stat
          icon={Timer}
          value={stats.amountRange ? `${formatEuro(stats.amountRange[0])} – ${formatEuro(stats.amountRange[1])}` : "–"}
          label="Bandbreedte van de geteste bedragen"
        />
      </div>

      <div className="mt-6 overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[820px] text-left text-sm">
          <caption className="sr-only">Meetresultaten per casino met {methodName}</caption>
          <thead className="bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th scope="col" className="px-4 py-3 font-medium">Casino</th>
              <th scope="col" className="px-4 py-3 font-medium">Bedrag</th>
              <th scope="col" className="px-4 py-3 font-medium">Storting</th>
              <th scope="col" className="px-4 py-3 font-medium">Snelheid</th>
              <th scope="col" className="px-4 py-3 font-medium">{payoutViaBank ? "Terug naar bank" : "Uitbetaling"}</th>
              <th scope="col" className="px-4 py-3 font-medium">KYC</th>
              <th scope="col" className="px-4 py-3 font-medium">Kosten</th>
              <th scope="col" className="px-4 py-3 font-medium">Apparaat</th>
              <th scope="col" className="px-4 py-3 font-medium">Getest op</th>
              <th scope="col" className="px-4 py-3 font-medium">Tester</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {stats.rows.map((row, i) => (
              <tr key={`${row.casinoSlug}-${i}`} className="bg-card">
                <th scope="row" className="px-4 py-3 font-medium text-foreground">{row.casino}</th>
                <td className="px-4 py-3 text-muted-foreground">{formatEuro(row.amount)}</td>
                <td className="px-4 py-3 text-muted-foreground">{row.depositSeconds} sec.</td>
                <td className="px-4 py-3">
                  <span className="flex h-2 w-24 overflow-hidden rounded-full bg-secondary" aria-hidden>
                    <span
                      className="h-full rounded-full bg-success"
                      style={{ width: `${Math.max(8, Math.round((row.depositSeconds / maxDeposit) * 100))}%` }}
                    />
                  </span>
                  <span className="sr-only">{row.depositSeconds} seconden</span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {row.withdrawalHours === null ? "Via bank/wallet" : `${row.withdrawalHours} uur`}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{row.kycRequired ? "Ja" : "Nee"}</td>
                <td className="px-4 py-3 text-muted-foreground">{row.fees}</td>
                <td className="px-4 py-3 text-muted-foreground">{row.device}</td>
                <td className="px-4 py-3 text-muted-foreground">{row.testedAt}</td>
                <td className="px-4 py-3 text-muted-foreground">{row.tester}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {untested.length > 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">
          Nog niet in deze testronde: {untested.map((c) => c.name).join(", ")}. Voor deze aanbieders tonen we
          alleen de informatie uit de kassa; meetresultaten volgen na een eigen test.
        </p>
      ) : null}
      <p className="mt-3 text-xs text-muted-foreground">
        Getest op {stats.devices.join(", ")} door {stats.testers.join(", ")}. Doorlooptijden zijn
        momentopnames en kunnen per bank, tijdstip en accountstatus verschillen.
      </p>

      {walkthrough ? <div className="mt-10"><Walkthrough methodName={methodName} w={walkthrough} /></div> : null}
    </section>
  );
}

function Walkthrough({ methodName, w }: { methodName: string; w: MethodWalkthrough }) {
  return (
    <section aria-labelledby="zo-getest" className="scroll-mt-28">
      <h2 id="zo-getest" className="text-xl font-semibold text-foreground">
        Hoe hebben wij {methodName} getest?
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{w.setup}</p>
      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="text-sm font-semibold text-foreground">Storten met {methodName}: wat we deden</h3>
          <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
            {w.deposit.map((step) => <li key={step}>{step}</li>)}
          </ol>
        </div>
        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="text-sm font-semibold text-foreground">Opnemen: zo kwam de winst binnen</h3>
          <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
            {w.withdrawal.map((step) => <li key={step}>{step}</li>)}
          </ol>
        </div>
      </div>
      <h3 className="mt-6 text-sm font-semibold text-foreground">Wanneer vroeg het casino om verificatie?</h3>
      <p className="mt-1 text-sm text-muted-foreground">{w.verification}</p>
      <h3 className="mt-6 text-sm font-semibold text-foreground">Waar liepen we tegenaan?</h3>
      <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
        {w.pitfalls.map((p) => <li key={p}>{p}</li>)}
      </ul>
      <div className="mt-6 rounded-lg border border-border bg-secondary/40 p-5 text-sm text-muted-foreground">
        <p><strong className="text-foreground">Ons oordeel:</strong> {methodName} past het best bij {w.bestFor.charAt(0).toLowerCase() + w.bestFor.slice(1)}</p>
        <p className="mt-2">
          <strong className="text-foreground">Liever iets anders?</strong>{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: w.alternative.slug }} className="text-primary underline-offset-2 hover:underline">
            {w.alternative.name}
          </Link>{" "}
          {w.alternative.why}
        </p>
        <p className="mt-2 text-xs">
          Lees hoe we meten en scoren in onze{" "}
          <Link to="/methodologie/" className="text-primary underline-offset-2 hover:underline">testmethode</Link>.
        </p>
      </div>
    </section>
  );
}
